import { useCallback, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { AdminVenueBreadcrumbDetailDto, ModerationContentType } from '@ivy-monorepo/shared'
import { BaseModalProps } from 'src/types/modal'
import BreadcrumbActivityBox from '../BreadcrumbActivityBox'
import { adminService } from 'src/services/admin.service'

interface VenueBreadcrumbModalProps extends BaseModalProps {
  publicId: string | null
  onDeleted?: (publicId: string) => void
  onModerationChange?: () => void
  onDelete: (publicId: string) => Promise<void>
}

const VenueBreadcrumbModal = ({
  isOpen,
  onClose,
  publicId,
  onDelete,
  onDeleted,
  onModerationChange,
}: VenueBreadcrumbModalProps) => {
  const [detail, setDetail] = useState<AdminVenueBreadcrumbDetailDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchDetail = useCallback(async () => {
    if (!publicId) {
      setDetail(null)
      return
    }

    try {
      setIsLoading(true)
      const response = await adminService.getVenueBreadcrumbDetail(publicId)
      setDetail(response)
    } catch (err) {
      console.log('Failed to fetch venue breadcrumb detail', err)
      setDetail(null)
    } finally {
      setIsLoading(false)
    }
  }, [publicId])

  useEffect(() => {
    if (!isOpen) {
      setDetail(null)
      return
    }

    fetchDetail()
  }, [fetchDetail, isOpen])

  const handleModerationChange = () => {
    onModerationChange?.()
  }

  const hasFlaggedReply = detail?.replies.some(reply => reply.isFlagged) ?? false

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className={`modal-popup confirm-popup breadcrumb-detail-popup${detail?.isFlagged ? ' moderation-flagged-content' : ''}`}
      overlayClassName="modal-overlay"
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onAfterClose={() => {
        document.body.classList.remove('ReactModal__Body--open')
      }}
    >
      <div className="modal-content-wrapper">
        <div className="moderation-modal-header">
          <h2>Breadcrumb</h2>
          <div className="moderation-modal-header-flags">
            {detail?.isFlagged && <span className="moderation-flag-badge">Breadcrumb flagged</span>}
            {hasFlaggedReply && <span className="moderation-flag-badge moderation-flag-badge--reply">Reply flagged</span>}
          </div>
        </div>

        {isLoading && <div className="breadcrumb-detail-loading">Loading breadcrumb details...</div>}

        {!isLoading && detail && (
          <div className="breadcrumb-activity-sections breadcrumb-detail-sections">
            <BreadcrumbActivityBox
              title="Breadcrumb"
              variant="breadcrumb"
              content={detail.content}
              reactions={detail.reactions}
              meta={[
                {
                  label: 'Author',
                  value: `${detail.author.firstName} ${detail.author.lastName}`,
                },
                { label: 'Venue', value: detail.venue.name },
                { label: 'Posted', value: detail.createdAt, type: 'datetime' },
              ]}
              moderation={{
                authorPublicId: detail.author.publicId,
                contentType: ModerationContentType.VenueBreadcrumb,
                contentReferenceId: detail.publicId,
                quotedContent: detail.content,
                isFlagged: detail.isFlagged,
                onFlagChange: flagged => {
                  setDetail(current => (current ? { ...current, isFlagged: flagged } : current))
                  handleModerationChange()
                },
                onDelete: async () => {
                  await onDelete(detail.publicId)
                  onDeleted?.(detail.publicId)
                },
                deleteConfirmTitle: 'Delete breadcrumb?',
                deleteConfirmBody:
                  'This will hide the breadcrumb and its replies from users. Content is retained for audit.',
              }}
            />

            {detail.replies.length > 0 && (
              <div className="breadcrumb-replies-group">
                <h3 className="breadcrumb-replies-heading">Replies ({detail.replies.length})</h3>
                {detail.replies.map(reply => (
                  <BreadcrumbActivityBox
                    key={reply.publicId}
                    title={`${reply.author.firstName} ${reply.author.lastName}'s reply`}
                    variant="reply"
                    content={reply.content}
                    reactions={reply.reactions}
                    meta={[
                      { label: 'Venue', value: detail.venue.name },
                      { label: 'Replied', value: reply.createdAt, type: 'datetime' },
                    ]}
                    moderation={{
                      authorPublicId: reply.author.publicId,
                      contentType: ModerationContentType.VenueBreadcrumbReply,
                      contentReferenceId: reply.publicId,
                      quotedContent: reply.content,
                      isFlagged: reply.isFlagged,
                      onFlagChange: flagged => {
                        setDetail(current =>
                          current
                            ? {
                                ...current,
                                replies: current.replies.map(item =>
                                  item.publicId === reply.publicId ? { ...item, isFlagged: flagged } : item
                                ),
                              }
                            : current
                        )
                        handleModerationChange()
                      },
                      onDelete: async () => {
                        await adminService.deleteVenueBreadcrumbReply(reply.publicId)
                        setDetail(current =>
                          current
                            ? {
                                ...current,
                                replies: current.replies.filter(item => item.publicId !== reply.publicId),
                              }
                            : current
                        )
                        handleModerationChange()
                      },
                      deleteConfirmTitle: 'Delete reply?',
                      deleteConfirmBody: 'This will hide this reply from users. Content is retained for audit.',
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="modal-footer-content">
          <button className="styled-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default VenueBreadcrumbModal
